import { token } from '@/styled-system/tokens';
import { resolveTokenValue } from '@/src/lib/utils/resolveTokenValue';

const AREA_BOUNDARY_STROKE_WEIGHT = 2;
const AREA_BOUNDARY_STROKE_OPACITY = 0.8;

/**
 * The SRP area boundary: a border-token outline, and the modal scrim over everything outside
 * the area. Google Maps draws the polygon itself and can't read CSS variables, so the tokens
 * are resolved for the current color mode on each call.
 */
export function getAreaBoundaryStyle() {
  const scrimOpacity = Number(resolveTokenValue(token.var('opacity.dialogScrim')));
  return {
    strokeColor: resolveTokenValue(token.var('colors.border.accent.brand.hero')),
    strokeOpacity: AREA_BOUNDARY_STROKE_OPACITY,
    strokeWeight: AREA_BOUNDARY_STROKE_WEIGHT,
    fillColor: resolveTokenValue(token.var('colors.bg.screen.scrim')),
    // An unresolved token (no stylesheet, as in jsdom) drops the shade instead of passing NaN.
    fillOpacity: Number.isFinite(scrimOpacity) ? scrimOpacity : 0,
  };
}

export type StaticMapBoundaryStyle = {
  /** Static Maps colors, `0xRRGGBBAA`. */
  outlineColor: string;
  outlineWeight: number;
  shadeColor: string;
};

let colorContext: CanvasRenderingContext2D | null | undefined;

// Panda emits the tokens as hsl(), and Static Maps only takes hex; canvas serializes an opaque color as #rrggbb.
function toStaticMapColor(color: string, opacity: number): string | null {
  if (typeof document === 'undefined' || color.startsWith('var(')) return null;
  colorContext ??= document.createElement('canvas').getContext('2d');
  if (!colorContext) return null;
  colorContext.fillStyle = '#000000';
  colorContext.fillStyle = color;
  const hex = String(colorContext.fillStyle);
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `0x${(hex.slice(1) + alpha).toUpperCase()}`;
}

/** The same boundary for a static map image, or null when the tokens don't resolve (on the server). */
export function getStaticMapBoundaryStyle(): StaticMapBoundaryStyle | null {
  const { strokeColor, strokeOpacity, strokeWeight, fillColor, fillOpacity } = getAreaBoundaryStyle();
  const outlineColor = toStaticMapColor(strokeColor, strokeOpacity);
  const shadeColor = toStaticMapColor(fillColor, fillOpacity);
  return outlineColor && shadeColor ? { outlineColor, outlineWeight: strokeWeight, shadeColor } : null;
}
